import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSectionDetailBySlug } from '@/lib/queries';
import { PageSkeleton, ContentRouteError } from '@/components/Skeleton';
import { AppBar } from '@/components/AppBar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useTweaks } from '@/lib/tweaks';
import {
  cancelCloudWrite,
  clearQuizState,
  flushCloudWrite,
  hydrateFromCloud,
  loadQuizState,
  quizKey,
  saveQuizState,
  type QuizStatus,
} from '@/lib/quizStorage';
import { deleteCloudProgress } from '@/lib/userProgress';
import { createAttempt } from '@/lib/attempts';
import { useAuth } from '@/lib/AuthProvider';
import { useUserCompletion } from '@/lib/userCompletion';
import { QuizReview } from '@/components/QuizReview';
import type { OptionOrder, QuestionDoc } from '@/lib/types';
import { AppError } from '@/lib/errors';

export const Route = createFileRoute('/courses/$courseSlug/sections/$sectionSlug')({
  loader: async ({ params }) => {
    const detail = await getSectionDetailBySlug(params.courseSlug, params.sectionSlug);
    if (!detail) {
      throw new AppError('Đề thi này chưa có hoặc đã bị xóa.', {
        title: 'Không tìm thấy đề',
      });
    }
    return detail;
  },
  pendingComponent: () => <PageSkeleton />,
  errorComponent: ({ error, reset }) => <ContentRouteError error={error} reset={reset} />,
  component: QuizScreen,
});

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

type Picks = Record<string, OptionOrder>;
type Crossed = Record<string, OptionOrder[]>;

function QuizScreen() {
  const { course, section, questions, audioUrl, audioUrls, imageUrls } = Route.useLoaderData();
  const navigate = useNavigate();
  const { tweaks } = useTweaks();
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const completion = useUserCompletion();
  const startedAtRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const replayCleanupRef = useRef<(() => void) | null>(null);
  const storageKey = quizKey(course.slug, section.slug);
  const hydratedRef = useRef<string | null>(null);

  const isPerQuestionAudio = course.audio_granularity === 'question';
  const isMondai2 = course.mondai_type === 2;
  const defaultReadingTime = course.default_reading_time_seconds ?? 15;

  // Per-question durations (only used when granularity='question').
  // Falls back to (end - start) if audio_duration_seconds missing.
  const durations = useMemo(
    () => questions.map((q) => q.audio_duration_seconds ?? Math.max(0, q.end_seconds - q.start_seconds)),
    [questions],
  );
  const cumDurations = useMemo(() => {
    const cum = [0];
    durations.forEach((d) => cum.push(cum[cum.length - 1] + d));
    return cum;
  }, [durations]);
  const totalVirtualDuration = cumDurations[cumDurations.length - 1] || 0;

  const [status, setStatus] = useState<QuizStatus>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(
    isPerQuestionAudio ? totalVirtualDuration : section.audio_duration_seconds || 0,
  );
  const [activeQIdx, setActiveQIdx] = useState(0);
  const [readingCountdown, setReadingCountdown] = useState<number | null>(null);
  const [picks, setPicks] = useState<Picks>({});
  const [crossed, setCrossed] = useState<Crossed>({});
  const [showScript, setShowScript] = useState<boolean>(tweaks.showScript);
  const [resumeOffered, setResumeOffered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resumeStateRef = useRef<ReturnType<typeof loadQuizState>>(null);
  const finishQuizRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const optCount = course.options_per_question || 3;

  const ranges = useMemo(
    () => {
      if (isPerQuestionAudio) {
        return questions.map((q, i) => ({
          id: q.id,
          question_number: q.question_number,
          start: cumDurations[i],
          end: cumDurations[i + 1],
        }));
      }
      return questions.map((q) => ({
        id: q.id,
        question_number: q.question_number,
        start: q.start_seconds,
        end: q.end_seconds,
      }));
    },
    [questions, isPerQuestionAudio, cumDurations],
  );

  const virtualToLocal = useCallback(
    (virtualSec: number): { qIdx: number; localTime: number } => {
      if (!isPerQuestionAudio) return { qIdx: 0, localTime: virtualSec };
      const clamped = Math.max(0, Math.min(virtualSec, totalVirtualDuration));
      for (let i = 0; i < cumDurations.length - 1; i++) {
        if (clamped >= cumDurations[i] && clamped < cumDurations[i + 1]) {
          return { qIdx: i, localTime: clamped - cumDurations[i] };
        }
      }
      const last = cumDurations.length - 2;
      return { qIdx: Math.max(0, last), localTime: durations[last] || 0 };
    },
    [isPerQuestionAudio, cumDurations, totalVirtualDuration, durations],
  );

  const currentIdx = useMemo(() => {
    if (ranges.length === 0) return -1;
    for (let i = 0; i < ranges.length; i++) {
      if (currentTime < ranges[i].end) return i;
    }
    return ranges.length - 1;
  }, [currentTime, ranges]);

  const currentQ: QuestionDoc | null = currentIdx >= 0 ? questions[currentIdx] : null;
  const isInIntro =
    currentQ !== null && currentTime < (ranges[currentIdx]?.start ?? 0);
  const hasNarrator = (ranges[0]?.start ?? 0) >= 15;
  const isNarrator = hasNarrator && currentTime < (ranges[0]?.start ?? 0);

  // Reset + load saved state on section change
  useEffect(() => {
    setStatus('idle');
    setPicks({});
    setCrossed({});
    setCurrentTime(0);
    setActiveQIdx(0);
    setShowScript(tweaks.showScript);
    startedAtRef.current = null;
    const audio = audioRef.current;
    if (audio) {
      // Audio `src` is managed imperatively (not a controlled JSX prop) — setting it via a
      // React attribute diff would re-trigger the browser's load algorithm and abort any
      // in-flight play() on every question change (see handleEnded). Set it once here.
      audio.src = isPerQuestionAudio ? audioUrls[0] || '' : audioUrl;
      audio.currentTime = 0;
    }

    const applySaved = (saved: ReturnType<typeof loadQuizState>) => {
      // Already-finished local state: skip resume banner and jump straight into review.
      if (saved && saved.status === 'finished') {
        resumeStateRef.current = null;
        setResumeOffered(false);
        setPicks(saved.picks);
        setCrossed(saved.crossed || {});
        setShowScript(saved.showLiveScript);
        setCurrentTime(saved.currentTime);
        setStatus('finished');
        return;
      }
      if (saved && (Object.keys(saved.picks).length > 0 || saved.currentTime > 5)) {
        resumeStateRef.current = saved;
        setResumeOffered(true);
      } else {
        resumeStateRef.current = null;
        setResumeOffered(false);
      }
    };

    // First paint: use local cache immediately.
    applySaved(loadQuizState(storageKey));

    // If logged in (incl. anonymous), pull cloud and merge.
    const hydrateKey = `${uid ?? ''}::${storageKey}`;
    if (uid && hydratedRef.current !== hydrateKey) {
      hydratedRef.current = hydrateKey;
      let cancelled = false;
      hydrateFromCloud(uid, course.slug, section.slug)
        .then((merged) => {
          if (cancelled) return;
          applySaved(merged);
        })
        .catch(() => {
          // already showing local cache
        });
      return () => {
        cancelled = true;
      };
    }
    // NOTE: tweaks.showScript is intentionally NOT a dependency — this effect resets the
    // in-progress quiz (picks/crossed/audio). Toggling a display option must never reset the
    // quiz (FR-006). showScript is kept in sync by the dedicated effect below.
  }, [section.id, section.slug, course.slug, storageKey, uid, isPerQuestionAudio, audioUrl, audioUrls]);

  // Reflect the "Hiện script" display toggle live WITHOUT touching quiz state (FR-006).
  // Skip the initial run so the section-load effect above owns the first value (saved state);
  // only subsequent toggles of the global preference propagate here.
  const scriptSyncMounted = useRef(false);
  useEffect(() => {
    if (!scriptSyncMounted.current) {
      scriptSyncMounted.current = true;
      return;
    }
    setShowScript(tweaks.showScript);
  }, [tweaks.showScript]);

  // Save picks/status changes immediately (sync to cloud debounced).
  // Note: when status === 'finished', quizStorage skips cloud sync (attempt doc owns that snapshot).
  useEffect(() => {
    if (status === 'idle' && Object.keys(picks).length === 0 && Object.keys(crossed).length === 0) return;
    // Record startedAt on first transition into a "working" state.
    if ((status === 'playing' || status === 'paused') && startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
    const audio = audioRef.current;
    const time = audio?.currentTime ?? 0;
    saveQuizState(
      storageKey,
      { picks, crossed, currentTime: time, status, showLiveScript: showScript },
      {
        uid,
        courseSlug: course.slug,
        sectionSlug: section.slug,
      },
    );
  }, [picks, crossed, status, showScript, storageKey, uid, course.slug, section.slug]);

  // Periodic local save while playing — skip cloud to avoid spamming Firestore on every tick.
  useEffect(() => {
    if (status !== 'playing') return;
    const id = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      saveQuizState(
        storageKey,
        {
          picks,
          crossed,
          currentTime: audio.currentTime,
          status,
          showLiveScript: showScript,
        },
        { skipCloud: true },
      );
    }, 3000);
    return () => clearInterval(id);
  }, [status, picks, crossed, showScript, storageKey]);

  // Flush any pending cloud writes when leaving the page.
  useEffect(() => {
    if (!uid) return;
    return () => {
      flushCloudWrite(uid, course.slug, section.slug);
    };
  }, [uid, course.slug, section.slug]);

  // Warn before unload while a quiz is mid-flight (audio playing or paused
  // with answers in progress). Browsers ignore the custom string and show their
  // own generic prompt — but the prompt itself is what matters.
  useEffect(() => {
    const inProgress =
      status === 'playing' ||
      (status === 'paused' && Object.keys(picks).length > 0);
    if (!inProgress) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for Chrome/Edge; Firefox/Safari just need preventDefault().
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status, picks]);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPerQuestionAudio) {
      // Virtual total duration is sum of per-question durations.
      // Keep authoritative `audioDuration` = totalVirtualDuration; individual loads
      // only refresh the durations array if pipeline value is missing.
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        // If we don't have a pipeline-provided duration for this question yet,
        // we could update, but for simplicity rely on Firestore values.
      }
      setAudioDuration(totalVirtualDuration);
    } else if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setAudioDuration(audio.duration);
    }
  }, [isPerQuestionAudio, totalVirtualDuration]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = isPerQuestionAudio ? cumDurations[activeQIdx] + audio.currentTime : audio.currentTime;
    setCurrentTime(t);
  }, [isPerQuestionAudio, cumDurations, activeQIdx]);

  // rAF loop for smooth progress bar while playing (browser timeupdate fires ~4Hz on iOS)
  const rafRef = useRef<number>(0);
  useEffect(() => {
    if (status !== 'playing') { cancelAnimationFrame(rafRef.current); return; }
    const tick = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        const t = isPerQuestionAudio ? cumDurations[activeQIdx] + audio.currentTime : audio.currentTime;
        setCurrentTime(t);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, isPerQuestionAudio, cumDurations, activeQIdx]);

  const handleEnded = useCallback(() => {
    if (isPerQuestionAudio && activeQIdx < questions.length - 1) {
      // Auto-advance to next question's MP3.
      const nextIdx = activeQIdx + 1;
      const nextQ = questions[nextIdx];
      const readingSec = nextQ?.reading_time_seconds ?? (isMondai2 ? defaultReadingTime : null);
      setActiveQIdx(nextIdx);
      const audio = audioRef.current;
      if (audio) {
        audio.src = audioUrls[nextIdx] || '';
        audio.currentTime = 0;
        if (readingSec && readingSec > 0) {
          // Mondai 2: pause for reading time before play.
          setReadingCountdown(readingSec);
          setStatus('paused');
        } else {
          audio.play().then(() => setStatus('playing')).catch(() => setStatus('paused'));
        }
      }
    } else {
      void finishQuizRef.current();
    }
  }, [isPerQuestionAudio, activeQIdx, questions, audioUrls, isMondai2, defaultReadingTime]);

  // Reading countdown effect (Mondai 2): tick down, auto-play when reaches 0.
  useEffect(() => {
    if (readingCountdown === null || readingCountdown <= 0) return;
    const id = setInterval(() => {
      setReadingCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          const audio = audioRef.current;
          if (audio) {
            audio.play().then(() => setStatus('playing')).catch(() => undefined);
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [readingCountdown]);

  const skipReadingTime = useCallback(() => {
    setReadingCountdown(null);
    const audio = audioRef.current;
    if (audio) {
      audio.play().then(() => setStatus('playing')).catch(() => undefined);
    }
  }, []);

  const clearReplay = useCallback(() => {
    if (replayCleanupRef.current) {
      replayCleanupRef.current();
      replayCleanupRef.current = null;
    }
  }, []);

  const playFrom = useCallback(
    (sec: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      clearReplay();
      const target = audioDuration > 0
        ? Math.max(0, Math.min(sec, audioDuration))
        : Math.max(0, sec);
      if (isPerQuestionAudio) {
        const { qIdx, localTime } = virtualToLocal(target);
        if (qIdx !== activeQIdx) {
          setActiveQIdx(qIdx);
          audio.src = audioUrls[qIdx] || '';
          audio.load();
        }
        audio.currentTime = localTime;
        setCurrentTime(target);
        audio.play().then(() => setStatus('playing')).catch(() => undefined);
      } else {
        audio.currentTime = target;
        setCurrentTime(target);
        audio.play().then(() => setStatus('playing')).catch(() => undefined);
      }
    },
    [audioDuration, clearReplay, isPerQuestionAudio, virtualToLocal, activeQIdx, audioUrls],
  );

  const start = useCallback(() => {
    if (isPerQuestionAudio && isMondai2) {
      // Set up first question with reading time.
      setActiveQIdx(0);
      const firstQ = questions[0];
      const readingSec = firstQ?.reading_time_seconds ?? defaultReadingTime;
      const audio = audioRef.current;
      if (audio && readingSec > 0) {
        audio.src = audioUrls[0] || '';
        audio.currentTime = 0;
        setCurrentTime(0);
        setReadingCountdown(readingSec);
        setStatus('paused');
        return;
      }
    }
    playFrom(0);
  }, [isPerQuestionAudio, isMondai2, questions, defaultReadingTime, audioUrls, playFrom]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => setStatus('playing')).catch(() => undefined);
  }, []);

  const togglePlay = useCallback(() => {
    if (status === 'playing') pause();
    else if (status === 'paused') resume();
    else if (status === 'idle') start();
  }, [status, pause, resume, start]);

  const restart = useCallback(() => {
    clearQuizState(storageKey);
    if (uid) {
      cancelCloudWrite(uid, course.slug, section.slug);
      deleteCloudProgress(uid, course.slug, section.slug).catch(() => {});
      completion.refresh();
    }
    setPicks({});
    setCrossed({});
    setShowScript(tweaks.showScript);
    setResumeOffered(false);
    resumeStateRef.current = null;
    startedAtRef.current = null;
    playFrom(0);
  }, [storageKey, playFrom, tweaks.showScript, uid, course.slug, section.slug, completion]);

  const resumeFromSaved = useCallback(() => {
    const saved = resumeStateRef.current;
    if (!saved) {
      setResumeOffered(false);
      return;
    }
    setPicks(saved.picks);
    setCrossed(saved.crossed || {});
    setShowScript(saved.showLiveScript);
    setCurrentTime(saved.currentTime);
    setResumeOffered(false);
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = saved.currentTime;
      setStatus('paused');
    }
  }, []);

  const skipBy = useCallback(
    (delta: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      clearReplay();
      if (isPerQuestionAudio) {
        const virtualNow = cumDurations[activeQIdx] + audio.currentTime;
        const target = Math.max(0, Math.min(virtualNow + delta, audioDuration));
        const { qIdx, localTime } = virtualToLocal(target);
        if (qIdx !== activeQIdx) {
          setActiveQIdx(qIdx);
          audio.src = audioUrls[qIdx] || '';
          audio.load();
        }
        audio.currentTime = localTime;
        setCurrentTime(target);
      } else {
        const target = audioDuration > 0
          ? Math.max(0, Math.min(audio.currentTime + delta, audioDuration))
          : Math.max(0, audio.currentTime + delta);
        audio.currentTime = target;
        setCurrentTime(target);
      }
    },
    [audioDuration, clearReplay, isPerQuestionAudio, cumDurations, activeQIdx, virtualToLocal, audioUrls],
  );

  const jumpToQ = useCallback(
    (idx: number) => {
      if (idx < -1 || idx >= ranges.length) return;
      const target = idx === -1 ? 0 : ranges[idx].start;
      playFrom(target);
    },
    [ranges, playFrom],
  );

  const replayQ = useCallback(() => {
    if (currentIdx >= 0) jumpToQ(currentIdx);
  }, [currentIdx, jumpToQ]);

  const replayQuestion = useCallback(
    (q: QuestionDoc) => {
      const audio = audioRef.current;
      if (!audio) return;
      // In review mode (finished), play audio without flipping back to active screen
      const inReview = status === 'finished';
      clearReplay();
      if (isPerQuestionAudio) {
        const qIdx = questions.findIndex((x) => x.id === q.id);
        if (qIdx < 0) return;
        if (qIdx !== activeQIdx) {
          setActiveQIdx(qIdx);
          audio.src = audioUrls[qIdx] || '';
          audio.load();
        }
        audio.currentTime = 0;
        if (!inReview) setCurrentTime(cumDurations[qIdx]);
        audio.play().then(() => { if (!inReview) setStatus('playing'); }).catch(() => undefined);
        const stopAtEnd = () => {
          audio.pause();
          if (!inReview) setStatus('paused');
          audio.removeEventListener('ended', stopAtEnd);
          replayCleanupRef.current = null;
        };
        audio.addEventListener('ended', stopAtEnd);
        replayCleanupRef.current = () => audio.removeEventListener('ended', stopAtEnd);
      } else {
        audio.currentTime = q.start_seconds;
        if (!inReview) setCurrentTime(q.start_seconds);
        audio.play().then(() => { if (!inReview) setStatus('playing'); }).catch(() => undefined);
        const onTime = () => {
          if (audio.currentTime >= q.end_seconds) {
            audio.pause();
            if (!inReview) setStatus('paused');
            audio.removeEventListener('timeupdate', onTime);
            replayCleanupRef.current = null;
          }
        };
        audio.addEventListener('timeupdate', onTime);
        replayCleanupRef.current = () => audio.removeEventListener('timeupdate', onTime);
      }
    },
    [clearReplay, isPerQuestionAudio, questions, activeQIdx, audioUrls, cumDurations, status],
  );

  const seekTo = useCallback(
    (t: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      clearReplay();
      if (isPerQuestionAudio) {
        const { qIdx, localTime } = virtualToLocal(t);
        if (qIdx !== activeQIdx) {
          setActiveQIdx(qIdx);
          audio.src = audioUrls[qIdx] || '';
          audio.load();
        }
        audio.currentTime = localTime;
        setCurrentTime(t);
      } else {
        audio.currentTime = t;
        setCurrentTime(t);
      }
    },
    [clearReplay, isPerQuestionAudio, virtualToLocal, activeQIdx, audioUrls],
  );

  // Pick for ANY specific question (used by row clicks)
  const setPickFor = useCallback((qid: string, opt: OptionOrder) => {
    setPicks((prev) => ({ ...prev, [qid]: opt }));
    // Picking an option auto-uncrosses it (can't both pick and eliminate)
    setCrossed((prev) => {
      const cur = prev[qid];
      if (!cur || !cur.includes(opt)) return prev;
      const next = cur.filter((o) => o !== opt);
      return { ...prev, [qid]: next };
    });
  }, []);

  // Toggle crossed-out (elimination mark) for a question/option
  const toggleCrossFor = useCallback((qid: string, opt: OptionOrder) => {
    setCrossed((prev) => {
      const cur = prev[qid] || [];
      const isCrossed = cur.includes(opt);
      const next = isCrossed ? cur.filter((o) => o !== opt) : [...cur, opt];
      return { ...prev, [qid]: next };
    });
    // If we're newly crossing an option that's currently picked, unpick it
    setPicks((prev) => {
      if (prev[qid] !== opt) return prev;
      const wasCrossed = (crossed[qid] || []).includes(opt);
      if (wasCrossed) return prev; // we're uncrossing → keep pick as-is
      const { [qid]: _removed, ...rest } = prev;
      void _removed;
      return rest;
    });
  }, [crossed]);

  // Pick for current question (used by keyboard 1/2/3)
  const pickCurrentAnswer = useCallback(
    (opt: OptionOrder) => {
      if (!currentQ || isInIntro || isNarrator) return;
      setPickFor(currentQ.id, opt);
    },
    [currentQ, isInIntro, isNarrator, setPickFor],
  );

  const answeredCount = Object.keys(picks).length;

  const finishQuiz = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    const audio = audioRef.current;
    if (audio) audio.pause();
    const time = audio?.currentTime ?? 0;
    const correctNow = questions.filter((q) => picks[q.id] === q.correct_option_order).length;
    const totalQ = questions.length;
    const answered = Object.keys(picks).length;
    const startedAt = startedAtRef.current;
    const durationSec = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : Math.round(time);

    if (uid) cancelCloudWrite(uid, course.slug, section.slug);

    // Persist locally and to cloud so other browsers/sessions also see the
    // finished state without needing to re-do the quiz.
    saveQuizState(
      storageKey,
      { picks, crossed, currentTime: time, status: 'finished', showLiveScript: showScript },
      { uid, courseSlug: course.slug, sectionSlug: section.slug, flushNow: true },
    );

    if (uid) {
      try {
        await createAttempt(uid, {
          courseSlug: course.slug,
          sectionSlug: section.slug,
          picks,
          crossed,
          correctCount: correctNow,
          totalQuestions: totalQ,
          answeredCount: answered,
          durationSeconds: durationSec,
        });
      } catch {
        // Even if attempt creation fails, still finish the UI so the user
        // can see their results. They can re-submit by re-finishing later.
      }
      completion.refresh();
    }

    startedAtRef.current = null;
    setStatus('finished');
    setSubmitting(false);
  }, [
    submitting,
    picks,
    crossed,
    showScript,
    storageKey,
    uid,
    course.slug,
    section.slug,
    questions,
    completion,
  ]);

  useEffect(() => {
    finishQuizRef.current = finishQuiz;
  }, [finishQuiz]);

  // Branded submit confirmation (replaces window.confirm). Pause audio while open; resume on cancel.
  const [submitConfirmSkipped, setSubmitConfirmSkipped] = useState<number | null>(null);
  const submitResumeRef = useRef(false);

  const submitNow = useCallback(() => {
    const skipped = questions.length - answeredCount;
    submitResumeRef.current = status === 'playing';
    if (status === 'playing') pause();
    setSubmitConfirmSkipped(skipped);
  }, [answeredCount, questions.length, status, pause]);

  const confirmSubmit = useCallback(() => {
    setSubmitConfirmSkipped(null);
    void finishQuiz();
  }, [finishQuiz]);

  const cancelSubmit = useCallback(() => {
    setSubmitConfirmSkipped(null);
    if (submitResumeRef.current) resume();
  }, [resume]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (status === 'idle' && e.key !== ' ' && e.key !== 'Spacebar') return;
      switch (e.key) {
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipBy(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipBy(10);
          break;
        case '1':
          pickCurrentAnswer(1);
          break;
        case '2':
          if (optCount >= 2) pickCurrentAnswer(2);
          break;
        case '3':
          if (optCount >= 3) pickCurrentAnswer(3);
          break;
        case '4':
          if (optCount >= 4) pickCurrentAnswer(4);
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [togglePlay, skipBy, pickCurrentAnswer, status, optCount]);

  useEffect(() => () => clearReplay(), [clearReplay]);

  // Auto-scroll current question into view while playing
  useEffect(() => {
    if (status !== 'playing' || isNarrator || !currentQ) return;
    const el = document.querySelector<HTMLElement>(`[data-qid="${currentQ.id}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const offset = rect.top + window.scrollY - 160; // sticky header + breathing room
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, [currentQ?.id, status, isNarrator]);

  const correctCount = useMemo(
    () => questions.filter((q) => picks[q.id] === q.correct_option_order).length,
    [picks, questions],
  );

  const isPlayingOrPaused = status === 'playing' || status === 'paused';
  // slim player ~70 + safe area (no floating submit FAB anymore). Pad ~110 to clear it.
  const bottomPad = isPlayingOrPaused ? 110 : 100;

  return (
    <div className="screen-enter" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <AppBar
        title={section.title_vi}
        subtitle={`JLPT ${course.level} · 問題${course.mondai_type} · ${course.mondai_name_jp}`}
        onBack={() =>
          navigate({
            to: '/levels/$level/exams/$examSlug',
            params: { level: course.level, examSlug: section.slug },
          })
        }
        right={
          isPlayingOrPaused ? (
            <HeaderSubmitBtn
              answered={answeredCount}
              total={questions.length}
              onSubmit={submitNow}
            />
          ) : null
        }
      />

      <audio
        ref={audioRef}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleLoadedMetadata}
        onEnded={handleEnded}
        playsInline
      />

      <main style={{ maxWidth: 480, margin: '0 auto', padding: `14px 16px ${bottomPad + 16}px` }}>
        {resumeOffered && resumeStateRef.current && (
          <ResumeBanner
            currentTime={resumeStateRef.current.currentTime}
            onResume={resumeFromSaved}
            onRestart={() => {
              clearQuizState(storageKey);
              setResumeOffered(false);
              resumeStateRef.current = null;
            }}
          />
        )}

        {status === 'idle' && (
          <IdleContent
            section={section}
            questions={questions}
            optionsPerQuestion={optCount}
          />
        )}

        {isPlayingOrPaused && currentQ && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {readingCountdown !== null && readingCountdown > 0 && (
              <ReadingCountdownBanner
                seconds={readingCountdown}
                questionNumber={questions[activeQIdx]?.question_number ?? activeQIdx + 1}
                totalQuestions={questions.length}
                onSkip={skipReadingTime}
              />
            )}
            <StatusBanner
              isNarrator={isNarrator}
              isInIntro={isInIntro}
              currentQ={currentQ}
              totalQ={questions.length}
              status={status}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {questions.map((q, i) => (
                <QuestionRow
                  key={q.id}
                  q={q}
                  imageUrl={imageUrls[i] ?? null}
                  optCount={optCount}
                  mondaiType={course.mondai_type}
                  picked={picks[q.id]}
                  crossed={crossed[q.id] || []}
                  isCurrent={i === currentIdx && !isNarrator}
                  isPlaying={i === currentIdx && !isNarrator && status === 'playing'}
                  isDone={picks[q.id] !== undefined}
                  showScript={showScript}
                  onPick={(opt) => setPickFor(q.id, opt)}
                  onCross={(opt) => toggleCrossFor(q.id, opt)}
                  onJump={() => jumpToQ(i)}
                />
              ))}
            </div>
          </div>
        )}

        {status === 'finished' && (
          <QuizReview
            questions={questions}
            picks={picks}
            correctCount={correctCount}
            answeredCount={answeredCount}
            optCount={optCount}
            onReplayRange={replayQuestion}
            courseId={course.id}
            sectionId={section.id}
          />
        )}
      </main>

      <ConfirmDialog
        open={submitConfirmSkipped !== null}
        title="Nộp bài"
        message={
          (submitConfirmSkipped ?? 0) > 0
            ? `Bạn còn ${submitConfirmSkipped} câu chưa trả lời. Nộp bài luôn?`
            : 'Nộp bài và xem kết quả?'
        }
        confirmLabel={(submitConfirmSkipped ?? 0) > 0 ? 'Nộp luôn' : 'Nộp bài'}
        cancelLabel="Tiếp tục làm"
        onConfirm={confirmSubmit}
        onCancel={cancelSubmit}
      />

      <BottomPlayer
        status={status}
        simTime={currentTime}
        duration={audioDuration}
        currentQ={currentQ}
        currentIdx={currentIdx}
        ranges={ranges}
        onStart={start}
        onTogglePlay={togglePlay}
        onSkip={skipBy}
        onJump={jumpToQ}
        onReplay={replayQ}
        onSeek={seekTo}
        onRestart={restart}
      />

      {submitting && <SubmittingOverlay />}
    </div>
  );
}

function SubmittingOverlay() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
          minWidth: 240,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: '3px solid var(--surface-2)',
            borderTopColor: 'var(--brand)',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>Đang nộp bài…</span>
          <span style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>Lưu kết quả lên tài khoản</span>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Resume banner ─────────────────────────────────────────────────── */
function ResumeBanner({
  currentTime,
  onResume,
  onRestart,
}: {
  currentTime: number;
  onResume: () => void;
  onRestart: () => void;
}) {
  return (
    <div
      style={{
        background: 'var(--brand-light)',
        border: '1px solid var(--brand-mid)',
        borderRadius: 14,
        padding: '14px 16px',
        marginBottom: 12,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--brand)' }}>📌 Đang dở quiz này</div>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>
        Tiếp tục từ {fmtTime(currentTime)}?
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          onClick={onResume}
          style={{
            background: 'var(--brand)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            borderRadius: 9,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Tiếp tục
        </button>
        <button
          type="button"
          onClick={onRestart}
          style={{
            background: 'transparent',
            color: 'var(--text-2)',
            border: 'none',
            cursor: 'pointer',
            borderRadius: 9,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Làm lại từ đầu
        </button>
      </div>
    </div>
  );
}

/* ── Answer row (full text — script-on mode) ───────────────────────── */
function AnswerRow({
  num,
  text,
  picked,
  crossed,
  onClick,
  onCross,
}: {
  num: number;
  text?: string;
  picked: boolean;
  crossed: boolean;
  onClick: () => void;
  onCross: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const dim = crossed && !picked;
  return (
    <div
      style={{
        width: '100%',
        minHeight: 48,
        borderRadius: 11,
        border: picked
          ? '2px solid var(--brand)'
          : crossed
          ? '1.5px dashed var(--text-3)'
          : '1.5px solid var(--border)',
        background: picked
          ? 'var(--brand-light)'
          : crossed
          ? 'var(--surface-2)'
          : 'var(--surface)',
        display: 'flex',
        alignItems: 'stretch',
        boxShadow: picked ? '0 3px 10px rgba(67,56,202,0.15)' : 'none',
        transform: pressed ? 'scale(0.99)' : 'scale(1)',
        transition: 'all 0.12s ease',
        WebkitTapHighlightColor: 'transparent',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        style={{
          flex: 1,
          minWidth: 0,
          padding: '11px 8px 11px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textAlign: 'left',
          opacity: dim ? 0.55 : 1,
        }}
      >
        <span
          style={{
            width: 16,
            height: 16,
            flexShrink: 0,
            borderRadius: '50%',
            border: `2px solid ${picked ? 'var(--brand)' : 'var(--text-3)'}`,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {picked && (
            <span
              style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand)' }}
            />
          )}
        </span>
        <span
          style={{
            fontSize: 15,
            fontWeight: 800,
            flexShrink: 0,
            color: picked ? 'var(--brand)' : 'var(--text-1)',
            fontFamily: 'var(--font-ui)',
            minWidth: 18,
            textDecoration: dim ? 'line-through' : 'none',
          }}
        >
          {num}.
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            fontFamily: 'var(--font-jp)',
            lineHeight: 1.55,
            color: 'var(--text-1)',
            fontWeight: 500,
            textDecoration: dim ? 'line-through' : 'none',
          }}
        >
          {text || '—'}
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCross();
        }}
        aria-label={crossed ? 'Bỏ đánh dấu sai' : 'Đánh dấu là đáp án sai'}
        title={crossed ? 'Bỏ đánh dấu sai' : 'Đánh dấu là đáp án sai'}
        style={{
          width: 40,
          flexShrink: 0,
          background: crossed ? 'var(--text-3)' : 'transparent',
          border: 'none',
          borderLeft: '1px solid var(--border)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 700,
          color: crossed ? '#fff' : 'var(--text-3)',
          fontFamily: 'var(--font-ui)',
          lineHeight: 1,
          padding: 0,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        ×
      </button>
    </div>
  );
}

/* ── Answer pill (radio + number — script-off mode) ────────────────── */
function AnswerPill({
  num,
  picked,
  onClick,
}: {
  num: number;
  picked: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        flex: 1,
        minHeight: 40,
        borderRadius: 9,
        border: picked ? '2px solid var(--brand)' : '1.5px solid var(--border)',
        background: picked ? 'var(--brand-light)' : 'var(--surface)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        boxShadow: picked ? '0 2px 6px rgba(67,56,202,0.18)' : 'none',
        transform: pressed ? 'scale(0.95)' : 'scale(1)',
        transition: 'all 0.12s ease',
        WebkitTapHighlightColor: 'transparent',
        padding: '4px',
      }}
    >
      <span
        style={{
          width: 13,
          height: 13,
          flexShrink: 0,
          borderRadius: '50%',
          border: `1.8px solid ${picked ? 'var(--brand)' : 'var(--text-3)'}`,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {picked && (
          <span
            style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }}
          />
        )}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: picked ? 'var(--brand)' : 'var(--text-1)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        {num}
      </span>
    </button>
  );
}

/* ── Question row — header + 3 horizontal answers (or full rows w/ script) ── */
function QuestionRow({
  q,
  optCount,
  mondaiType,
  picked,
  crossed,
  isCurrent,
  isPlaying,
  isDone,
  showScript,
  onPick,
  onCross,
  onJump,
  imageUrl,
}: {
  q: QuestionDoc;
  imageUrl: string | null;
  optCount: number;
  mondaiType: number;
  picked: OptionOrder | undefined;
  crossed: OptionOrder[];
  isCurrent: boolean;
  isPlaying: boolean;
  isDone: boolean;
  showScript: boolean;
  onPick: (opt: OptionOrder) => void;
  onCross: (opt: OptionOrder) => void;
  onJump: () => void;
}) {
  const [showDialogue, setShowDialogue] = useState(false);
  const hasDialogue = !!(q.full_script && q.full_script.trim() && q.full_script !== q.prompt_text);
  // M1+M2+M3: setup question chỉ nghe audio, không in giấy thi → hint khi chưa show script
  const setupAudioOnly = (mondaiType === 1 || mondaiType === 2 || mondaiType === 3) && !!q.prompt_text;
  // M1+M2: paper có options text → render AnswerRow. M3+M4: pills số (audio-only).
  const showOptionsAsText = mondaiType === 1 || mondaiType === 2;
  return (
    <div
      data-qid={q.id}
      style={{
        background: 'var(--surface)',
        borderRadius: 12,
        padding: '10px 12px',
        border: isCurrent ? '1.5px solid var(--brand)' : '1px solid var(--border)',
        boxShadow: isCurrent ? '0 4px 14px rgba(67,56,202,0.16)' : 'var(--shadow-sm)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div
          style={{
            width: 24,
            height: 24,
            flexShrink: 0,
            background: isCurrent ? 'var(--brand)' : 'var(--surface-2)',
            borderRadius: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            color: isCurrent ? '#fff' : 'var(--text-2)',
            fontFamily: 'var(--font-ui)',
          }}
        >
          {q.question_number}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {isCurrent && (
            <>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--brand)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)' }}>
                Đang phát
              </span>
            </>
          )}
          {!isCurrent && isDone && (
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>
              Đã chọn câu {picked}
            </span>
          )}
          {!isCurrent && !isDone && (
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>—</span>
          )}
        </div>

        <button
          type="button"
          onClick={onJump}
          aria-label={isPlaying ? `Đang phát câu ${q.question_number}` : `Nghe câu ${q.question_number}`}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 6px',
            fontSize: 11,
            color: isPlaying ? 'var(--brand)' : 'var(--text-3)',
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {isPlaying ? (
            <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 11 }}>
              {[0, 1, 2].map((bar) => (
                <span
                  key={bar}
                  style={{
                    width: 2.5,
                    height: '100%',
                    borderRadius: 1,
                    background: 'var(--brand)',
                    transformOrigin: 'bottom',
                    animation: `eqBar 0.9s ease-in-out ${bar * 0.15}s infinite`,
                  }}
                />
              ))}
            </span>
          ) : (
            '▶'
          )}
        </button>
      </div>

      {/* Prompt — only in script mode (M1+M2 paper KHÔNG có setup, chỉ nghe audio) */}
      {showScript && q.prompt_text && (
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--surface-2)',
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          <p
            style={{
              fontSize: 14,
              fontFamily: 'var(--font-jp)',
              color: 'var(--text-1)',
              lineHeight: 1.6,
            }}
          >
            {q.prompt_text}
          </p>
        </div>
      )}
      {/* Hint khi prompt ẩn (M1+M2+M3): nhắc user là setup hỏi qua audio */}
      {!showScript && setupAudioOnly && isCurrent && (
        <div
          style={{
            padding: '6px 10px',
            background: 'var(--amber-bg, #FEF3C7)',
            borderRadius: 8,
            marginBottom: 8,
            fontSize: 11,
            color: 'var(--amber, #92400E)',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          🎧 Câu hỏi được đọc trong audio — lắng nghe để chọn đáp án
        </div>
      )}

      {/* Dialogue toggle — show full_script when expanded */}
      {hasDialogue && (
        <>
          <button
            type="button"
            onClick={() => setShowDialogue((v) => !v)}
            style={{
              background: showDialogue ? 'var(--brand-light)' : 'var(--surface-2)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 11,
              fontWeight: 600,
              color: showDialogue ? 'var(--brand)' : 'var(--text-2)',
              fontFamily: 'var(--font-ui)',
              marginBottom: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            💬 {showDialogue ? 'Ẩn nội dung' : 'Xem nội dung'}
          </button>
          {showDialogue && (
            <div
              style={{
                padding: '12px 14px',
                background: 'var(--surface-2)',
                borderRadius: 10,
                marginBottom: 10,
                fontSize: 13,
                fontFamily: 'var(--font-jp)',
                color: 'var(--text-1)',
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {q.full_script}
            </div>
          )}
        </>
      )}

      {imageUrl && (
        <img
          src={imageUrl}
          alt={`Tranh minh hoạ câu ${q.question_number}`}
          loading="lazy"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            borderRadius: 10,
            border: '1px solid var(--border)',
            marginBottom: 10,
          }}
        />
      )}

      {/* Answers — M1+M2 default full-text rows (giấy thi có options text);
           M3+M4 audio-only → pills số. Toggle "Hiện script" override pills→rows. */}
      {(showScript || showOptionsAsText) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: optCount }, (_, i) => {
            const order = (i + 1) as OptionOrder;
            const opt = q.options.find((o) => o.order === order);
            return (
              <AnswerRow
                key={order}
                num={order}
                text={opt?.text}
                picked={picked === order}
                crossed={crossed.includes(order)}
                onClick={() => onPick(order)}
                onCross={() => onCross(order)}
              />
            );
          })}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          {Array.from({ length: optCount }, (_, i) => {
            const order = (i + 1) as OptionOrder;
            return (
              <AnswerPill
                key={order}
                num={order}
                picked={picked === order}
                onClick={() => onPick(order)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Status banner ─────────────────────────────────────────────────── */
function StatusBanner({
  isNarrator,
  isInIntro,
  currentQ,
  totalQ,
  status,
}: {
  isNarrator: boolean;
  isInIntro: boolean;
  currentQ: QuestionDoc;
  totalQ: number;
  status: QuizStatus;
}) {
  let bg = 'var(--brand-light)';
  let color = 'var(--brand)';
  let icon = '🎯';
  let title = `Câu ${currentQ.question_number} / ${totalQ}`;
  let subtitle = 'Chọn đáp án phù hợp nhất';

  if (status === 'paused') {
    bg = 'var(--surface-2)';
    color = 'var(--text-2)';
    icon = '⏸';
    title = 'Đã tạm dừng';
    subtitle = `Bấm ▶ để tiếp tục câu ${currentQ?.question_number ?? 1}`;
  } else if (isNarrator) {
    bg = 'var(--amber-bg)';
    color = 'var(--amber)';
    icon = '📖';
    title = 'Đang nghe hướng dẫn';
    subtitle = 'Câu 1 sắp đến — tập trung nhé';
  } else if (isInIntro) {
    bg = 'var(--amber-bg)';
    color = 'var(--amber)';
    icon = '🎧';
    title = `Câu ${currentQ.question_number} sắp bắt đầu`;
    subtitle = 'Tập trung nghe stimulus';
  }

  return (
    <div
      style={{
        background: bg,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          background: 'rgba(255,255,255,0.6)',
          borderRadius: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color, lineHeight: 1.25 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3 }}>{subtitle}</div>
      </div>
    </div>
  );
}

/* ── Dialogue toggle (per question, result mode) ───────────────────── */
/* ── Reading countdown banner (Mondai 2) ───────────────────────────── */
function ReadingCountdownBanner({
  seconds,
  questionNumber,
  totalQuestions,
  onSkip,
}: {
  seconds: number;
  questionNumber: number;
  totalQuestions: number;
  onSkip: () => void;
}) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FEF3C7 0%, #FBBF24 100%)',
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 4px 12px rgba(251, 191, 36, 0.25)',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 800,
          color: '#92400E',
          flexShrink: 0,
        }}
      >
        {seconds}s
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#78350F' }}>
          ⏱ Câu {questionNumber}/{totalQuestions} · Đọc đề
        </div>
        <div style={{ fontSize: 12, color: '#92400E', marginTop: 3, lineHeight: 1.4 }}>
          Đọc kỹ câu hỏi + 4 đáp án. Audio sẽ tự phát sau {seconds}s.
        </div>
      </div>
      <button
        type="button"
        onClick={onSkip}
        style={{
          background: '#fff',
          color: '#92400E',
          border: 'none',
          borderRadius: 10,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        ▶ Bỏ qua
      </button>
    </div>
  );
}

/* ── Header submit button ──────────────────────────────────────────── */
function HeaderSubmitBtn({
  answered,
  total,
  onSubmit,
}: {
  answered: number;
  total: number;
  onSubmit: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const complete = answered >= total;
  return (
    <button
      type="button"
      onClick={onSubmit}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: complete ? 'var(--correct)' : 'var(--brand)',
        border: 'none',
        cursor: 'pointer',
        borderRadius: 999,
        padding: '7px 12px',
        color: '#fff',
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        transform: pressed ? 'scale(0.95)' : 'scale(1)',
        transition: 'all 0.14s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ fontSize: 13 }}>✓</span>
      <span>Nộp bài</span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          background: 'rgba(255,255,255,0.22)',
          padding: '2px 7px',
          borderRadius: 999,
          letterSpacing: 0.3,
        }}
      >
        {answered}/{total}
      </span>
    </button>
  );
}

/* ── Compact player control ────────────────────────────────────────── */
function CompactBtn({
  children,
  onClick,
  disabled,
  primary,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  title?: string;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        minWidth: primary ? 40 : 32,
        height: primary ? 40 : 32,
        borderRadius: primary ? 12 : 9,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: primary ? 'var(--brand)' : 'rgba(255,255,255,0.08)',
        color: '#fff',
        fontSize: primary ? 16 : 12,
        fontFamily: 'var(--font-ui)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.3 : pressed ? 0.7 : 1,
        transform: pressed && !disabled ? 'scale(0.9)' : 'scale(1)',
        transition: 'all 0.1s',
        boxShadow: primary ? '0 3px 10px rgba(67,56,202,0.5)' : 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {children}
    </button>
  );
}

/* ── Bottom-fixed slim player (~64px) ──────────────────────────────── */
function BottomPlayer({
  status,
  simTime,
  duration,
  currentIdx,
  ranges,
  onStart,
  onTogglePlay,
  onSkip,
  onJump,
  onReplay,
  onSeek,
  onRestart,
}: {
  status: QuizStatus;
  simTime: number;
  duration: number;
  currentQ: QuestionDoc | null;
  currentIdx: number;
  ranges: Array<{ id: string; question_number: number; start: number; end: number }>;
  onStart: () => void;
  onTogglePlay: () => void;
  onSkip: (delta: number) => void;
  onJump: (idx: number) => void;
  onReplay: () => void;
  onSeek: (t: number) => void;
  onRestart: () => void;
}) {
  const isIdle = status === 'idle';
  const isFinished = status === 'finished';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        background: 'rgba(20,16,46,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.2)',
      }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '8px 12px 9px' }}>
        {isIdle ? (
          <button
            type="button"
            onClick={onStart}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--brand)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 12,
              padding: '12px',
              color: '#fff',
              fontFamily: 'var(--font-ui)',
              fontSize: 15,
              fontWeight: 700,
              boxShadow: '0 4px 16px rgba(67,56,202,0.5)',
            }}
          >
            ▶ Bắt đầu nghe
          </button>
        ) : isFinished ? (
          <button
            type="button"
            onClick={onRestart}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--brand)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 12,
              padding: '12px',
              color: '#fff',
              fontFamily: 'var(--font-ui)',
              fontSize: 15,
              fontWeight: 700,
              boxShadow: '0 4px 16px rgba(67,56,202,0.5)',
            }}
          >
            ↻ Làm lại từ đầu
          </button>
        ) : (
          <>
            <input
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={simTime}
              onChange={(e) => onSeek(Number(e.target.value))}
              style={{
                width: '100%',
                accentColor: 'var(--player-accent)',
                cursor: 'pointer',
                height: 3,
                marginBottom: 6,
                display: 'block',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.7)',
                  minWidth: 70,
                }}
              >
                {fmtTime(simTime)}
                <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 2px' }}>/</span>
                {fmtTime(duration)}
              </span>

              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <CompactBtn
                  onClick={() => onJump(currentIdx - 1)}
                  disabled={currentIdx <= 0}
                  title="Câu trước"
                >
                  ⏮
                </CompactBtn>
                <CompactBtn onClick={() => onSkip(-10)} title="-10s">
                  <span style={{ fontSize: 10, fontWeight: 700 }}>−10</span>
                </CompactBtn>
                <CompactBtn
                  primary
                  onClick={onTogglePlay}
                  title={status === 'playing' ? 'Dừng' : 'Tiếp tục'}
                >
                  {status === 'playing' ? '⏸' : '▶'}
                </CompactBtn>
                <CompactBtn onClick={() => onSkip(10)} title="+10s">
                  <span style={{ fontSize: 10, fontWeight: 700 }}>+10</span>
                </CompactBtn>
                <CompactBtn
                  onClick={() => onJump(currentIdx + 1)}
                  disabled={currentIdx >= ranges.length - 1}
                  title="Câu sau"
                >
                  ⏭
                </CompactBtn>
              </div>

              <button
                type="button"
                onClick={onReplay}
                title="Nghe lại câu"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '5px 8px',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 700,
                  minWidth: 36,
                }}
              >
                🔁
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Idle content ──────────────────────────────────────────────────── */
function IdleContent({
  section,
  questions,
  optionsPerQuestion,
}: {
  section: { title_vi: string; audio_duration_seconds: number };
  questions: QuestionDoc[];
  // Varies by 問題 (3 for 即時応答, 4 elsewhere) and by level, so the hints must be derived,
  // never hardcoded — a 4-option 問題 previously told learners to press "1 / 2 / 3".
  optionsPerQuestion: number;
}) {
  const optionKeys = Array.from({ length: optionsPerQuestion }, (_, i) => i + 1).join(' / ');
  // Real total: prefer the section duration; else sum per-question durations (question-granular
  // audio). Hide the estimate entirely when unknown — never render "~0 phút" (FR-008).
  const totalSeconds =
    section.audio_duration_seconds > 0
      ? section.audio_duration_seconds
      : questions.reduce(
          (s, q) => s + (q.audio_duration_seconds ?? Math.max(0, q.end_seconds - q.start_seconds)),
          0,
        );
  const minutes = totalSeconds > 0 ? Math.ceil(totalSeconds / 60) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          background: 'linear-gradient(140deg, var(--brand) 0%, var(--brand-dark) 100%)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px 22px',
          boxShadow: 'var(--shadow-md)',
          color: '#fff',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          Sẵn sàng chưa?
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, fontFamily: 'var(--font-jp)' }}>
          {section.title_vi}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>
          {questions.length} câu{minutes !== null ? ` · ≈ ${minutes} phút` : ''}
        </div>
      </div>

      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-md)',
          padding: '18px 18px 20px',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-1)', marginBottom: 14 }}>
          Hướng dẫn
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {[
            ['Nghe liền mạch từ đầu đến cuối', 'như đi thi thật'],
            [`Khi câu phát, bấm ${optionKeys}`, 'để chọn đáp án'],
            ['Sau khi xong', 'xem kết quả và kịch bản đầy đủ'],
          ].map(([t, sub], i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  background: 'var(--brand-light)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 800,
                  color: 'var(--brand)',
                }}
              >
                {i + 1}
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, lineHeight: 1.4 }}>{t}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
        <div
          className="kbd-hint"
          style={{
            marginTop: 16,
            fontSize: 11,
            color: 'var(--text-3)',
            padding: '9px 12px',
            background: 'var(--surface-2)',
            borderRadius: 8,
            fontFamily: 'monospace',
            textAlign: 'center',
          }}
        >
          Space: play/pause · ←→: tua 10s · 1–{optionsPerQuestion}: chọn
        </div>
      </div>
    </div>
  );
}

